/**
 * polight-home.js — scene engine for the ph-* home sections.
 *
 * One rAF loop, dimensions cached until something resizes, native scroll.
 * Without this file every scene is static and complete; with reduced
 * motion the scenes stay static and the film never starts on its own.
 */
(() => {
  if (window.polightHome) return;
  window.polightHome = true;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(min-width: 1000px)');
  const phone = matchMedia('(max-width: 699px)');
  const saveData = navigator.connection && navigator.connection.saveData;
  const clamp = value => Math.max(0, Math.min(1, value));
  const span = (p, from, to) => clamp((p - from) / (to - from));
  const easeInOut = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const easeOut = t => 1 - Math.pow(1 - t, 3);

  let heroes = [];
  let series = [];
  let frame = 0;
  let dirty = true;

  /* ---------------------------------------------------------------- *
   * Hero: the sun rises in the disc once its photograph is decoded,
   * then the scroll flies the disc into the ceiling of the room.
   * ---------------------------------------------------------------- */
  function lightUp(root) {
    if (root.dataset.lit) return;
    root.dataset.lit = 'true';
    requestAnimationFrame(() => root.classList.add('is-lit'));
  }

  function setupHero(root) {
    const motion = !reduced.matches;
    root.dataset.motion = motion ? 'on' : 'off';
    const q = selector => root.querySelector(selector);
    const hero = {
      root,
      motion,
      track: q('[data-ph-hero-track]'),
      stage: q('[data-ph-hero-stage]'),
      visual: q('[data-ph-hero-visual]'),
      roomImg: q('[data-ph-hero-room] img'),
      disc: q('[data-ph-hero-disc]'),
      glow: q('[data-ph-hero-glow]'),
      note: q('[data-ph-hero-note]'),
      lamp: (root.dataset.lamp || '59.5,29.5,21.4,9.9').split(',').map(n => parseFloat(n) / 100),
      roomAr: parseFloat(root.dataset.roomAr) || 0.5626
    };

    const sun = hero.disc && hero.disc.querySelector('img');
    if (!motion || !sun) {
      root.classList.add('is-lit');
    } else {
      const ready = sun.complete && sun.naturalWidth
        ? Promise.resolve()
        : new Promise(resolve => {
            sun.addEventListener('load', resolve, { once: true });
            sun.addEventListener('error', resolve, { once: true });
          });
      ready
        .then(() => (sun.decode ? sun.decode().catch(() => {}) : null))
        .then(() => lightUp(root));
      // Never leave a visitor in the dark.
      setTimeout(() => lightUp(root), 2600);
    }
    return hero;
  }

  function objectPosition(img) {
    const parts = getComputedStyle(img).objectPosition.split(' ');
    const read = value => (value && value.endsWith('%') ? parseFloat(value) / 100 : 0.5);
    return [read(parts[0]), read(parts[1])];
  }

  function measureHero(hero) {
    if (!hero.stage || !hero.visual) return;
    const stage = hero.stage.getBoundingClientRect();
    const box = hero.visual.getBoundingClientRect();
    const V = { x: box.left - stage.left, y: box.top - stage.top, w: box.width, h: box.height };

    // Where the lamp sits inside the room photograph after object-fit: cover.
    let dw = V.w;
    let dh = V.w / hero.roomAr;
    if (dh < V.h) {
      dh = V.h;
      dw = V.h * hero.roomAr;
    }
    const [px, py] = hero.roomImg ? objectPosition(hero.roomImg) : [0.5, 0.5];
    const ox = (V.w - dw) * px;
    const oy = (V.h - dh) * py;
    const [lx, ly, lw, lh] = hero.lamp;
    const T = { x: V.x + ox + lx * dw, y: V.y + oy + ly * dh, rx: lw * dw, ry: lh * dh };

    const wide = V.w > 560 && !phone.matches;
    const d0 = Math.max(160, Math.min(V.w * (wide ? 0.78 : 0.84), V.h * (wide ? 0.72 : 0.8)));
    hero.start = { x: V.x + V.w / 2, y: V.y + V.h * 0.5, d: d0 };
    hero.target = T;
    hero.tilt = (Math.acos(Math.min(1, T.ry / T.rx)) * 180) / Math.PI;
    hero.soft = Math.max(V.w, V.h) * 0.42;
    const cx = T.x - V.x;
    const cy = T.y - V.y;
    hero.reach = Math.max(Math.hypot(cx, cy), Math.hypot(V.w - cx, cy), Math.hypot(cx, V.h - cy), Math.hypot(V.w - cx, V.h - cy));

    const style = hero.root.style;
    style.setProperty('--ph-d0', `${d0.toFixed(1)}px`);
    style.setProperty('--ph-lx', `${cx.toFixed(1)}px`);
    style.setProperty('--ph-ly', `${cy.toFixed(1)}px`);

    // The label hangs from the rim, lower right; flipped left if it would leave the window.
    if (hero.note) {
      const angle = (52 * Math.PI) / 180;
      const ax = T.x + T.rx * Math.cos(angle);
      const ay = T.y + T.ry * Math.sin(angle);
      const room = V.x + V.w - ax - 16;
      hero.note.dataset.side = room < 170 ? 'left' : 'right';
      hero.note.style.setProperty('--nx', `${ax.toFixed(1)}px`);
      hero.note.style.setProperty('--ny', `${ay.toFixed(1)}px`);
      hero.note.classList.add('is-placed');
    }

    const track = hero.track.getBoundingClientRect();
    hero.top = track.top + scrollY;
    hero.travel = Math.max(1, track.height - hero.stage.offsetHeight);
  }

  function renderHero(hero, p) {
    const style = hero.root.style;
    if (!hero.motion) {
      style.setProperty('--ph-note', '1');
      style.setProperty('--ph-note-pe', 'auto');
      return;
    }
    const S = hero.start;
    const T = hero.target;
    if (!S || !T) return;

    // Flight: centre of the window -> the lamp in the ceiling, tilting back.
    const e = easeInOut(span(p, 0.03, 0.62));
    const x = S.x + (T.x - S.x) * e;
    const y = S.y + (T.y - S.y) * e;
    const scale = (S.d + (2 * T.rx - S.d) * e) / S.d;
    const tilt = hero.tilt * e;
    const move = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) translate(-50%, -50%)`;
    if (hero.disc) {
      hero.disc.style.transform = `${move} perspective(2400px) rotateX(${tilt.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
      hero.disc.style.opacity = (1 - span(p, 0.58, 0.74)).toFixed(3);
    }
    if (hero.glow) {
      hero.glow.style.transform = `${move} scale(${scale.toFixed(4)})`;
      hero.glow.style.opacity = (1 - span(p, 0.08, 0.5)).toFixed(3);
    }

    // The room: lit from the lamp outwards.
    const r0 = -hero.soft + (hero.reach + hero.soft * 1.2) * easeOut(span(p, 0.1, 0.66));
    style.setProperty('--ph-r0', `${r0.toFixed(1)}px`);
    style.setProperty('--ph-r1', `${(r0 + hero.soft).toFixed(1)}px`);
    style.setProperty('--ph-room-o', span(p, 0.06, 0.2).toFixed(3));
    style.setProperty('--ph-dim', (0.55 * (1 - span(p, 0.2, 0.7))).toFixed(3));

    const note = span(p, 0.72, 0.88);
    style.setProperty('--ph-note', note.toFixed(3));
    style.setProperty('--ph-note-pe', note > 0.5 ? 'auto' : 'none');
    style.setProperty('--ph-cue', (1 - span(p, 0, 0.1)).toFixed(3));
  }

  /* ---------------------------------------------------------------- *
   * Series: the scroll picks the active series on desktop.
   * ---------------------------------------------------------------- */
  function setupSeries(root) {
    const motion = !reduced.matches && desktop.matches;
    root.dataset.motion = motion ? 'on' : 'off';
    const items = Array.from(root.querySelectorAll('[data-ph-series-item]'));
    const slides = Array.from(root.querySelectorAll('[data-ph-series-slide]'));
    const count = root.querySelector('[data-ph-series-count]');
    const state = {
      root,
      items,
      slides,
      count,
      motion,
      track: root.querySelector('[data-ph-series-track]'),
      sticky: root.querySelector('[data-ph-series-sticky]'),
      active: -1
    };
    setActive(state, 0);
    return state;
  }

  function setActive(state, index) {
    if (index === state.active) return;
    state.active = index;
    state.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
    state.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
      // Warm the next photograph before the visitor gets there.
      if (Math.abs(i - index) <= 1) slide.querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; });
    });
    if (state.count) state.count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(state.items.length).padStart(2, '0')}`;
  }

  /* ---------------------------------------------------------------- *
   * Render loop
   * ---------------------------------------------------------------- */
  function measure(element) {
    const rect = element.getBoundingClientRect();
    return { top: rect.top + scrollY, height: rect.height };
  }

  function render() {
    frame = 0;
    const y = scrollY;
    const vh = innerHeight;

    if (dirty) {
      heroes.forEach(measureHero);
      series.forEach(state => {
        if (!state.track) return;
        const box = measure(state.track);
        state.top = box.top;
        state.travel = Math.max(1, box.height - (state.sticky ? state.sticky.offsetHeight : vh));
      });
      dirty = false;
    }

    heroes.forEach(hero => {
      if (!hero.track || hero.top === undefined) return;
      if (hero.top > y + vh || hero.top + hero.travel + vh < y) return;
      renderHero(hero, clamp((y - hero.top) / hero.travel));
    });

    series.forEach(state => {
      if (!state.motion || !state.track) return;
      if (state.top > y + vh * 2 || state.top + state.travel + vh < y) return;
      const p = clamp((y - state.top) / state.travel);
      state.root.style.setProperty('--ph-p', p.toFixed(4));
      const index = Math.min(state.items.length - 1, Math.floor(p * state.items.length * 0.999));
      setActive(state, index);
    });
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(render);
  }

  /* ---------------------------------------------------------------- *
   * Drawings: complete without JS; with JS they draw once in view.
   * ---------------------------------------------------------------- */
  let revealObserver;
  function setupReveals() {
    if (revealObserver) revealObserver.disconnect();
    const panels = Array.from(document.querySelectorAll('[data-ph-reveal]'));
    if (!panels.length || reduced.matches || !('IntersectionObserver' in window)) {
      panels.forEach(panel => { panel.dataset.phReveal = ''; });
      return;
    }
    revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.3 });
    panels.forEach(panel => {
      if (panel.classList.contains('is-in')) return;
      panel.dataset.phReveal = 'wait';
      revealObserver.observe(panel);
    });
  }

  /* ---------------------------------------------------------------- *
   * Film: plays only while seen, never on reduced motion or Save-Data.
   * ---------------------------------------------------------------- */
  let filmObserver;
  function setupFilms() {
    if (filmObserver) filmObserver.disconnect();
    const films = Array.from(document.querySelectorAll('[data-ph-film]'));
    if (!films.length) return;

    const load = film => {
      const video = film.querySelector('video');
      if (!video || video.dataset.loaded) return video;
      video.src = phone.matches ? video.dataset.mobile : video.dataset.desktop;
      video.dataset.loaded = 'true';
      return video;
    };
    const play = film => {
      const video = load(film);
      if (!video) return;
      // Loop only the part of the clip we want; the rest of the file may
      // show something else (here: a person talking to camera after 13 s).
      const end = parseFloat(film.dataset.end) || 0;
      if (end && !video.dataset.segment) {
        video.dataset.segment = 'true';
        video.addEventListener('timeupdate', () => {
          if (video.currentTime >= end - 0.5) film.classList.add('is-looping');
          if (video.currentTime >= end) video.currentTime = 0;
        });
        video.addEventListener('seeked', () => { if (video.currentTime < 1) film.classList.remove('is-looping'); });
      }
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => film.classList.remove('is-playing'));
      film.classList.add('is-playing');
      film.querySelector('[data-ph-film-toggle]')?.setAttribute('aria-label', film.dataset.pause);
    };
    const pause = film => {
      const video = film.querySelector('video');
      if (video && !video.paused) video.pause();
      film.classList.remove('is-playing');
      film.querySelector('[data-ph-film-toggle]')?.setAttribute('aria-label', film.dataset.play);
    };

    films.forEach(film => {
      const toggle = film.querySelector('[data-ph-film-toggle]');
      if (toggle && !toggle.dataset.bound) {
        toggle.dataset.bound = 'true';
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          if (film.classList.contains('is-playing')) {
            film.dataset.userPaused = 'true';
            pause(film);
          } else {
            delete film.dataset.userPaused;
            play(film);
          }
        });
      }
    });

    filmObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const film = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
          if (!reduced.matches && !saveData && !film.dataset.userPaused && !document.hidden) play(film);
        } else {
          pause(film);
        }
      });
    }, { threshold: [0, 0.35] });
    films.forEach(film => filmObserver.observe(film));
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) document.querySelectorAll('[data-ph-film].is-playing').forEach(film => film.querySelector('video')?.pause());
  });

  /* ---------------------------------------------------------------- *
   * Klaviyo forms: open the popup when Klaviyo is on the page; when it
   * is blocked or not loaded, the link goes to its own page instead.
   * ---------------------------------------------------------------- */
  const formShown = formId => Boolean(document.querySelector(
    `.klaviyo-form-${formId}, [data-testid="klaviyo-form-${formId}"], .kl-private-reset-css-Xuajs1[role="dialog"], div[aria-label][role="dialog"] form[data-testid="klaviyo-form-${formId}"]`
  ));

  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-ph-klaviyo]');
    if (!trigger || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const formId = trigger.getAttribute('data-ph-klaviyo');
    if (!formId) return;
    event.preventDefault();
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
    // Klaviyo blocked or slow: after a moment, fall back to the page.
    const fallback = trigger.getAttribute('href');
    setTimeout(() => {
      if (!formShown(formId) && fallback && fallback !== '#') location.href = fallback;
    }, 1800);
  });

  /* ---------------------------------------------------------------- *
   * Lifecycle
   * ---------------------------------------------------------------- */
  const resize = new ResizeObserver(() => { dirty = true; schedule(); });

  function collect() {
    resize.disconnect();
    heroes = Array.from(document.querySelectorAll('[data-ph-hero]')).map(setupHero);
    series = Array.from(document.querySelectorAll('[data-ph-series]')).map(setupSeries);
    heroes.forEach(hero => {
      if (hero.track) resize.observe(hero.track);
      if (hero.visual) resize.observe(hero.visual);
    });
    series.forEach(state => state.track && resize.observe(state.track));
    resize.observe(document.documentElement);
    setupReveals();
    setupFilms();
    dirty = true;
    schedule();
  }

  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', () => { dirty = true; schedule(); }, { passive: true });
  addEventListener('pageshow', () => { dirty = true; schedule(); });
  reduced.addEventListener('change', collect);
  desktop.addEventListener('change', collect);
  phone.addEventListener('change', collect);
  document.addEventListener('shopify:section:load', collect);
  document.addEventListener('shopify:section:unload', () => queueMicrotask(collect));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', collect, { once: true });
  else collect();
})();
